import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

// The Copy roster dialog on Admin — extracted verbatim (no behavior change).
export function CopyRosterDialog({
  open, onOpenChange, currentTeamName, adminTierOtherTeams, copySourceTeamId, onLoadSourcePlayers,
  copyLoading, copySourcePlayers, copySelected, onToggleSelectAllCopy, onToggleCopySelect,
  copyConflicts, copyOverrides, setCopyOverrides, copyError, copySaving, onSubmit, inputCls,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg" data-testid="copy-roster-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Copy roster</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Copy players from another team you administer into {currentTeamName}. This creates brand new, independent players — it won't remove or link them to the source team.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Source team</label>
            <select value={copySourceTeamId} onChange={(e) => onLoadSourcePlayers(e.target.value)} data-testid="copy-roster-source-select" className={inputCls}>
              <option value="" className="bg-[#121212]">— Select a team —</option>
              {adminTierOtherTeams.map((t) => (
                <option key={t.id} value={t.id} className="bg-[#121212]">{t.name}</option>
              ))}
            </select>
          </div>
          {copyLoading && <p className="text-zinc-500 text-sm">Loading roster…</p>}
          {!copyLoading && copySourceTeamId && copySourcePlayers.length === 0 && (
            <p className="text-zinc-600 text-sm" data-testid="copy-roster-source-empty">That team has no players to copy.</p>
          )}
          {copySourcePlayers.length > 0 && (
            <div className="border border-zinc-800 rounded-sm max-h-64 overflow-y-auto" data-testid="copy-roster-player-list">
              <label className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800 bg-[#0A0A0A] cursor-pointer">
                <input type="checkbox" checked={copySelected.size === copySourcePlayers.length} onChange={onToggleSelectAllCopy} data-testid="copy-roster-select-all" className="w-4 h-4 accent-[#CCFF00]" />
                <span className="text-xs font-label text-zinc-400 uppercase">Select all ({copySourcePlayers.length})</span>
              </label>
              {copySourcePlayers.map((p) => {
                const conflict = copyConflicts.find((c) => c.player_id === p.id);
                return (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-900 last:border-b-0" data-testid={`copy-roster-player-${p.jersey_number}`}>
                    <input type="checkbox" checked={copySelected.has(p.id)} onChange={() => onToggleCopySelect(p.id)} data-testid={`copy-roster-checkbox-${p.jersey_number}`} className="w-4 h-4 accent-[#CCFF00]" />
                    <span className="font-heading text-lg text-[#CCFF00] w-8">{p.jersey_number}</span>
                    <span className="text-white text-sm flex-1 truncate">{p.first_name} {p.last_name}</span>
                    {conflict && (
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-amber-400 text-[10px] font-label uppercase">#{conflict.jersey_number} taken</span>
                        <input
                          type="number"
                          min="0"
                          value={copyOverrides[p.id] ?? conflict.jersey_number}
                          onChange={(e) => setCopyOverrides({ ...copyOverrides, [p.id]: parseInt(e.target.value, 10) || 0 })}
                          data-testid={`copy-roster-override-${p.jersey_number}`}
                          className="w-16 bg-[#0A0A0A] border border-amber-500/50 text-white text-xs px-2 py-1 rounded-sm focus:outline-none focus:border-[#CCFF00]"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {copyError && <p className="text-[#FF3B30] text-sm" data-testid="copy-roster-error">{copyError}</p>}
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="font-label text-xs px-4 py-2 text-zinc-400 hover:text-white">Cancel</button>
          <button type="button" onClick={onSubmit} disabled={copySaving || copySelected.size === 0} data-testid="confirm-copy-roster-btn" className="bg-[#CCFF00] text-black font-label text-xs px-5 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
            {copySaving ? "Copying…" : copyConflicts.length > 0 ? "Resolve & copy" : "Copy roster"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
